import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take, finalize } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshInProgress = false;
  private refreshSubject = new Subject<void>(); // sygnalizuje zakończony refresh

  constructor(private auth: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // zawsze wysyłaj cookies
    const withCreds = req.clone({ withCredentials: true });

    return next.handle(withCreds).pipe(
      catchError((err: any) => {
        if (!this.shouldHandle401(err, withCreds)) {
          return throwError(() => err);
        }
        // 401 – obsłuż rolling refresh
        return this.handle401(withCreds, next);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshInProgress) {
      this.refreshInProgress = true;

      return this.auth.refresh().pipe(
        switchMap(() => {
          // po udanym refreshu przepuść oczekujących
          this.refreshSubject.next();
          return next.handle(req.clone({ withCredentials: true }));
        }),
        catchError((refreshErr) => {
          // refresh nie wyszedł – twarde wylogowanie
          this.auth.hardLogout();
          return throwError(() => refreshErr);
        }),
        finalize(() => {
          this.refreshInProgress = false;
        })
      );
    }

    // jeśli refresh już leci – poczekaj
    return this.refreshSubject.pipe(
      take(1),
      switchMap(() => next.handle(req.clone({ withCredentials: true })))
    );
  }

  private shouldHandle401(err: any, req: HttpRequest<any>): boolean {
    // obsługuj tylko 401 z API (nie assets) i nie wchodź w pętlę na /auth/refresh
    const isHttp = err instanceof HttpErrorResponse;
    if (!isHttp) return false;

    const status401 = err.status === 401;
    const isRefresh = /\/auth\/refresh\b/i.test(req.url);
    const isLogin = /\/auth\/login\b/i.test(req.url);
    const isAssets = /^\/?assets\//i.test(req.url);

    // Pomijamy OPTIONS (preflight)
    const isPreflight = req.method === 'OPTIONS';

    return status401 && !isRefresh && !isLogin && !isAssets && !isPreflight;
  }
}
